/**
 * Global Error Boundary
 * Catches unhandled errors and provides fallback UI
 */

import { captureException } from '../monitoring/sentry.js';
import logger from './logger.js';

/**
 * Error context for Sentry reporting
 */
interface ErrorContext {
  type: string;
  filename?: string;
  line?: number;
  column?: number;
  [key: string]: unknown;
}

let errorBoundaryInstalled = false;
let fallbackUIShown = false;

/**
 * Initialize the global error boundary
 * This should be called early in the application lifecycle
 */
export function initErrorBoundary(): void {
  if (errorBoundaryInstalled) {
    logger.warn('Error boundary already installed');
    return;
  }

  // Handle uncaught errors
  window.addEventListener('error', (event: ErrorEvent): void => {
    logger.error('Uncaught error', {
      message: event.message,
      filename: event.filename,
      line: event.lineno,
      column: event.colno,
      error: event.error
    });

    // Capture to Sentry with context
    const context: ErrorContext = {
      type: 'unhandled_error',
      filename: event.filename,
      line: event.lineno,
      column: event.colno
    };
    captureException(event.error || new Error(event.message), context);

    // Show fallback UI for critical errors
    if (!fallbackUIShown) {
      showFallbackUI(event.error || new Error(event.message));
    }

    // Prevent default browser error handling
    event.preventDefault();
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent): void => {
    logger.error('Unhandled promise rejection', {
      reason: event.reason,
      promise: event.promise
    });

    // Capture to Sentry
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason));
    
    captureException(error, {
      type: 'unhandled_rejection'
    });

    // Show fallback UI for critical promise rejections
    if (!fallbackUIShown) {
      showFallbackUI(error);
    }

    // Prevent default browser error handling
    event.preventDefault();
  });

  errorBoundaryInstalled = true;
  logger.info('Global error boundary installed');
}

/**
 * Show fallback UI when a critical error occurs
 */
function showFallbackUI(error: Error): void {
  fallbackUIShown = true;

  // Create fallback UI container
  const fallback = document.createElement('div');
  fallback.className = 'error-boundary-fallback';
  fallback.setAttribute('role', 'alert');
  fallback.setAttribute('aria-live', 'assertive');

  // Create error content
  fallback.innerHTML = `
    <div class="error-boundary-content">
      <div class="error-boundary-icon">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="48" height="48">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
      </div>
      <h1 class="error-boundary-title">Something went wrong</h1>
      <p class="error-boundary-message">
        We're sorry, but the application encountered an unexpected error. 
        Please try reloading the page.
      </p>
      <div class="error-boundary-actions">
        <button class="error-boundary-reload-btn">
          Reload Page
        </button>
      </div>
      <details class="error-boundary-details">
        <summary>Error Details</summary>
        <pre class="error-boundary-stack">${escapeHtml(error.stack || error.message || 'Unknown error')}</pre>
      </details>
    </div>
  `;

  // Add to document
  document.body.appendChild(fallback);
  
  // Attach event listener to reload button
  const reloadBtn = fallback.querySelector('.error-boundary-reload-btn');
  if (reloadBtn) {
    reloadBtn.addEventListener('click', () => {
      window.location.reload();
    });
  }

  logger.info('Fallback UI displayed');
}

/**
 * Escape HTML to prevent XSS in error messages
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Reset the error boundary state
 * Useful for testing or recovery
 */
export function resetErrorBoundary(): void {
  // Remove fallback UI if present
  const fallback = document.querySelector('.error-boundary-fallback');
  if (fallback) {
    fallback.remove();
  }
  
  fallbackUIShown = false;
  logger.info('Error boundary reset');
}

export default {
  initErrorBoundary,
  resetErrorBoundary
};
